/**
 * Orchard cart error rewriter (HOR-37).
 *
 * Shopify's storefront cart API returns its own error message when the
 * customer tries to set a line-item quantity higher than inventory:
 *
 *   "Only N item(s) was added to your cart due to availability."
 *
 * That string is built server-side and not customizable through the theme's
 * locale file. component-cart-items.js then drops it verbatim into
 * `.cart-item__error-text`. Since CLAUDE.md says we don't edit Horizon's
 * non-Orchard JS modules, we intercept after the fact: an attribute
 * observer watches the per-line error containers and rewrites the text
 * once Horizon un-hides them.
 *
 * If Shopify changes the wording, the regex below may stop matching —
 * we fall back to the original message in that case.
 */
(() => {
  const QUANTITY_ERROR_RE = /(\d+)\s+item(s)?\b/i;

  /** @param {string | null | undefined} errorText */
  const rewrite = (errorText) => {
    if (!errorText) return null;
    const match = errorText.match(QUANTITY_ERROR_RE);
    if (!match || !match[1]) return null;
    const n = parseInt(match[1], 10);
    return `Only ${n} ${n === 1 ? 'item' : 'items'} available at this time.`;
  };

  /** @param {Element} container */
  const handleErrorContainer = (container) => {
    if (container.classList.contains('hidden')) return;
    const text = container.querySelector('.cart-item__error-text');
    if (!text) return;
    const rewritten = rewrite(text.textContent);
    if (rewritten && text.textContent !== rewritten) {
      text.textContent = rewritten;
    }
  };

  const containerObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.target instanceof Element) {
        handleErrorContainer(m.target);
      }
    }
  });

  /** @param {Document | Element} root */
  const attachObservers = (root) => {
    const containers = root.querySelectorAll('.cart-items__error');
    for (const c of containers) {
      containerObserver.observe(c, { attributes: true, attributeFilter: ['class'] });
    }
  };

  const start = () => {
    attachObservers(document);
    // Cart re-renders (morph) replace the error containers; re-attach.
    new MutationObserver(() => attachObservers(document)).observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
